import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import RichTextEditor from "../../Common/TinyMCE/RichTextEditor";
import "react-toastify/dist/ReactToastify.css";
import {
  getContentSectionById,
  addContentSection,
  updateContentSection,
} from "../../../services/contentSectionsService";
import { validateContentSection } from "../../../utils/validation";
import { generateSlug } from "../../../utils/cmsUtils";
import { SECTION_LAYOUT_OPTIONS } from "./sectionConstants";
import { handleErrors } from "../../../utils/errorHandler";
import { usePageLevelAccess } from "../../../hooks/usePageLevelAccess";
import { Loading } from "../../Common/OtherElements/Loading";
import AiGenerate from "../../AI/AiGenerate";
import { CONTENT_SECTION_AI_RESPONSE_FIELD_MAP } from "../../AI/AIResponseFieldMap";
import "../../AI/ai-generator.css";

const INITIAL_FORM_STATE = {
  sectionName: "",
  sectionSlug: "",
  sectionDescription: "",
  sectionLayout: "",
  isFeatured: false,
  isFeaturedHome: false,
  displayOrder: "",
  metaTitle: "",
  metaDescription: "",
  metaKeywords: "",
};

const INITIAL_ERRORS_STATE = {
  sectionName: "",
  sectionSlug: "",
  sectionLayout: "",
  displayOrder: "",
  metaTitle: "",
  metaDescription: "",
};

const mapSectionResponse = (data) => ({
  sectionName: data.sectionName || data.SectionName || "",
  sectionSlug: data.sectionSlug || data.SectionSlug || "",
  sectionDescription: data.sectionDescription || data.SectionDescription || "",
  sectionLayout: data.sectionLayout || data.SectionLayout || "",
  isFeatured: Boolean(data.isFeatured ?? data.IsFeatured ?? false),
  isFeaturedHome: Boolean(data.isFeaturedHome ?? data.IsFeaturedHome ?? false),
  displayOrder:
    data.displayOrder !== undefined && data.displayOrder !== null
      ? String(data.displayOrder)
      : data.DisplayOrder !== undefined && data.DisplayOrder !== null
        ? String(data.DisplayOrder)
        : "",
  metaTitle: data.metaTitle || data.MetaTitle || "",
  metaDescription: data.metaDescription || data.MetaDescription || "",
  metaKeywords: data.metaKeywords || data.MetaKeywords || "",
});

export const AddContentSection = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);
  const [formData, setFormData] = useState(INITIAL_FORM_STATE);
  const [errors, setErrors] = useState(INITIAL_ERRORS_STATE);
  const [loading, setLoading] = useState(false);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [pageLevelAccessUrl, setPageLevelAccessUrl] = useState("");
  const slugManuallyEdited = useRef(false);

  const aiPageLink = useMemo(
    () => (isEditMode ? "content-sections/update/:id" : "content-sections/add"),
    [isEditMode],
  );

  const sectionAIRequiredFields = useMemo(
    () => [
      {
        getValue: (fd) => fd.sectionName,
        minLength: 3,
        errorMsg: "Please Enter Section Name To Generate",
        dynamicFieldKey: "SectionName",
      },
    ],
    [],
  );

  useEffect(() => {
    if (id) {
      setPageLevelAccessUrl("content-sections/update/:id");
    } else {
      setPageLevelAccessUrl("content-sections/add");
    }
  }, [id]);

  const { pageAccessData } = usePageLevelAccess(pageLevelAccessUrl);

  useEffect(() => {
    if (pageAccessData) {
      if (id) {
        if (!pageAccessData.editAccess) {
          navigate("/404-error-page");
        }
      } else if (!pageAccessData.addAccess) {
        navigate("/404-error-page");
      }
    }
  }, [pageAccessData, id, navigate]);

  useEffect(() => {
    const fetchData = async () => {
      if (id) {
        setLoading(true);
        try {
          const data = await getContentSectionById(id);
          setFormData(mapSectionResponse(data));
          slugManuallyEdited.current = true;
        } catch (error) {
          handleErrors(error);
        } finally {
          setLoading(false);
        }
      } else {
        setFormData(INITIAL_FORM_STATE);
        slugManuallyEdited.current = false;
      }
    };
    fetchData();
  }, [id]);

  const handleInputChange = ({ target: { name, value } }) => {
    if (name === "sectionName") {
      setFormData((prev) => ({
        ...prev,
        sectionName: value,
        ...(slugManuallyEdited.current
          ? {}
          : { sectionSlug: generateSlug(value) }),
      }));
    } else if (name === "sectionSlug") {
      slugManuallyEdited.current = true;
      setFormData((prev) => ({ ...prev, sectionSlug: value }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleToggleChange = ({ target: { name, checked } }) => {
    setFormData((prev) => ({ ...prev, [name]: checked }));
  };

  const handleDisplayOrderChange = (e) => {
    const { value } = e.target;
    if (value === "" || /^[1-9]\d*$/.test(value)) {
      setFormData((prev) => ({ ...prev, displayOrder: value }));
      setErrors((prev) => ({ ...prev, displayOrder: "" }));
    }
  };

  const handleDisplayOrderKeyDown = (e) => {
    if (["e", "E", "+", "-", "."].includes(e.key)) {
      e.preventDefault();
    }
  };

  const handleEditorChange = useCallback((content) => {
    setFormData((prev) => ({
      ...prev,
      sectionDescription: content ?? "",
    }));
  }, []);

  const aiEditorHandlers = useMemo(
    () => ({
      sectionDescription: handleEditorChange,
    }),
    [handleEditorChange],
  );

  const handleAiAfterInject = useCallback(() => {
    setFormData((prev) => {
      if (!slugManuallyEdited.current && prev.sectionName?.trim()) {
        return {
          ...prev,
          sectionSlug: generateSlug(String(prev.sectionName).trim()),
        };
      }
      return prev;
    });
  }, []);

  const handleSubmit = useCallback(
    async (e) => {
      console.log(formData);
      console.log("engtered here");
      debugger;
      e.preventDefault();
      const { valid, errors: validationErrors } =
        validateContentSection(formData);
      setErrors(validationErrors);

      if (!valid) return;

      const payload = {
        sectionName: formData.sectionName.trim(),
        sectionSlug: formData.sectionSlug.trim(),
        sectionDescription: formData.sectionDescription || "",
        sectionLayout: formData.sectionLayout,
        isFeatured: Boolean(formData.isFeatured),
        isFeaturedHome: Boolean(formData.isFeaturedHome),
        displayOrder: Number(formData.displayOrder),
        metaTitle: formData.metaTitle?.trim() || "",
        metaDescription: formData.metaDescription?.trim() || "",
        metaKeywords: formData.metaKeywords?.trim() || "",
      };

      if (isEditMode) {
        payload.id = id;
      }

      try {
        setIsButtonDisabled(true);
        const action = isEditMode ? updateContentSection : addContentSection;
        const response = await action(payload);

        if (response?.data?.isSuccess === false) {
          toast.error(
            response.data.message || "Request failed. Please try again.",
          );
          return;
        }

        toast.success(
          `Content section ${isEditMode ? "updated" : "added"} successfully!`,
        );

        // navigate("/content-sections");
        setTimeout(() => {
          navigate("/content-sections");
        }, 2000);
      } catch (error) {
        handleErrors(error);
      } finally {
        setIsButtonDisabled(false);
      }
    },
    [formData, isEditMode, id, navigate],
  );

  if (loading && isEditMode) {
    return <Loading />;
  }

  return (
    <>
      <div className="row">
        <div className="col-12">
          <div className="page-title-box d-sm-flex align-items-center justify-content-between">
            <h4 className="mb-sm-0">
              {isEditMode ? "Update Content Section" : "Add Content Section"}
            </h4>
            <div className="page-title-right">
              <ol className="breadcrumb m-0">
                <li className="breadcrumb-item">
                  <Link to="/">
                    <i className="ri-home-2-fill"></i>
                  </Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="/cms/content-sections">Content Sections</Link>
                </li>
                <li className="breadcrumb-item active">
                  {isEditMode ? `Update #${id}` : "Add"}
                </li>
              </ol>
            </div>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} method="POST">
        <div className="row">
          <div className="col-lg-8">
            <div className="card mt-xxl-n5">
              <div className="card-header">
                <h5 className="mb-0">Basic Information</h5>
              </div>
              <div className="card-body p-4">
                <div className="row">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label htmlFor="sectionName" className="form-label">
                        Section Name <span className="required-field">*</span>
                      </label>
                      <input
                        type="text"
                        name="sectionName"
                        id="sectionName"
                        placeholder="e.g. Energy Intelligence"
                        value={formData.sectionName}
                        onChange={handleInputChange}
                        className={`form-control ${
                          errors.sectionName ? "is-invalid" : ""
                        }`}
                      />
                      {errors.sectionName && (
                        <div className="invalid-feedback">
                          {errors.sectionName}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="mb-3">
                      <label htmlFor="sectionSlug" className="form-label">
                        Section Slug <span className="required-field">*</span>
                      </label>
                      <input
                        type="text"
                        name="sectionSlug"
                        id="sectionSlug"
                        placeholder="section-slug"
                        value={formData.sectionSlug}
                        onChange={handleInputChange}
                        className={`form-control ${
                          errors.sectionSlug ? "is-invalid" : ""
                        }`}
                      />
                      {errors.sectionSlug && (
                        <div className="invalid-feedback">
                          {errors.sectionSlug}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="mb-3">
                      <label htmlFor="sectionLayout" className="form-label">
                        Section Layout <span className="required-field">*</span>
                      </label>
                      <select
                        name="sectionLayout"
                        id="sectionLayout"
                        value={formData.sectionLayout}
                        onChange={handleInputChange}
                        className={`form-select ${
                          errors.sectionLayout ? "is-invalid" : ""
                        }`}
                      >
                        <option value="">Select layout</option>
                        {SECTION_LAYOUT_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                      {errors.sectionLayout && (
                        <div className="invalid-feedback d-block">
                          {errors.sectionLayout}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="col-12">
                    <div className="mb-3">
                      <label className="form-label">Section Description</label>
                      <RichTextEditor
                        value={formData.sectionDescription}
                        onEditorChange={handleEditorChange}
                        height={400}
                        defaultKeyword={formData.sectionName}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="accordion custom-accordionwithicon accordion-flush mt-3">
              <div className="accordion-item">
                <h2 className="accordion-header" id="seoSettingsHeading">
                  <button
                    className="accordion-button collapsed"
                    type="button"
                    data-bs-toggle="collapse"
                    data-bs-target="#seoSettingsCollapse"
                    aria-expanded="false"
                    aria-controls="seoSettingsCollapse"
                  >
                    <i className="ri-search-eye-line me-2"></i>
                    SEO Settings
                  </button>
                </h2>
                <div
                  id="seoSettingsCollapse"
                  className="accordion-collapse collapse"
                  aria-labelledby="seoSettingsHeading"
                >
                  <div className="accordion-body">
                    <div className="mb-3">
                      <label htmlFor="metaTitle" className="form-label">
                        Meta Title
                      </label>
                      <input
                        type="text"
                        name="metaTitle"
                        id="metaTitle"
                        placeholder="Enter meta title"
                        value={formData.metaTitle}
                        onChange={handleInputChange}
                        className={`form-control ${
                          errors.metaTitle ? "is-invalid" : ""
                        }`}
                      />
                      {errors.metaTitle && (
                        <div className="invalid-feedback">
                          {errors.metaTitle}
                        </div>
                      )}
                    </div>
                    <div className="mb-3">
                      <label htmlFor="metaDescription" className="form-label">
                        Meta Description
                      </label>
                      <textarea
                        name="metaDescription"
                        id="metaDescription"
                        rows="4"
                        placeholder="Enter meta description"
                        value={formData.metaDescription}
                        onChange={handleInputChange}
                        className={`form-control ${
                          errors.metaDescription ? "is-invalid" : ""
                        }`}
                      />
                      {errors.metaDescription && (
                        <div className="invalid-feedback">
                          {errors.metaDescription}
                        </div>
                      )}
                    </div>
                    <div className="mb-0">
                      <label htmlFor="metaKeywords" className="form-label">
                        Meta Keywords
                      </label>
                      <textarea
                        name="metaKeywords"
                        id="metaKeywords"
                        rows="3"
                        placeholder="Enter meta keywords (comma separated)"
                        value={formData.metaKeywords}
                        onChange={handleInputChange}
                        className="form-control"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-lg-4">
            <div className="card mt-xxl-n5">
              <div className="card-header">
                <h5 className="mb-0">Display Settings</h5>
              </div>
              <div className="card-body p-4">
                <div className="mb-3">
                  <label className="form-label d-block">Featured</label>
                  <div className="form-check form-switch form-switch-custom form-switch-primary">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      id="isFeatured"
                      name="isFeatured"
                      checked={formData.isFeatured}
                      onChange={handleToggleChange}
                    />
                    <label className="form-check-label" htmlFor="isFeatured">
                      Mark as featured section
                    </label>
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label d-block">Featured Home</label>
                  <div className="form-check form-switch form-switch-custom form-switch-primary">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      id="isFeaturedHome"
                      name="isFeaturedHome"
                      checked={formData.isFeaturedHome}
                      onChange={handleToggleChange}
                    />
                    <label
                      className="form-check-label"
                      htmlFor="isFeaturedHome"
                    >
                      Show on homepage featured area
                    </label>
                  </div>
                </div>

                <div className="mb-0">
                  <label htmlFor="displayOrder" className="form-label">
                    Display Order <span className="required-field">*</span>
                  </label>
                  <input
                    type="number"
                    name="displayOrder"
                    id="displayOrder"
                    min="1"
                    step="1"
                    placeholder="Enter display order"
                    value={formData.displayOrder}
                    onChange={handleDisplayOrderChange}
                    onKeyDown={handleDisplayOrderKeyDown}
                    className={`form-control ${
                      errors.displayOrder ? "is-invalid" : ""
                    }`}
                  />
                  {errors.displayOrder && (
                    <div className="invalid-feedback">
                      {errors.displayOrder}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <AiGenerate
              pageLink={aiPageLink}
              formData={formData}
              setFormData={setFormData}
              requiredFields={sectionAIRequiredFields}
              fieldMap={CONTENT_SECTION_AI_RESPONSE_FIELD_MAP}
              editorFields={["sectionDescription"]}
              editorHandlers={aiEditorHandlers}
              onAfterInject={handleAiAfterInject}
              title="Generate Section"
            />
          </div>
        </div>

        <div className="row mt-3">
          <div className="col-12">
            <button
              type="submit"
              className="btn btn-secondary pt-1 pb-1 p-3"
              disabled={isButtonDisabled}
            >
              {isButtonDisabled
                ? isEditMode
                  ? "Updating"
                  : "Saving"
                : isEditMode
                  ? "Update"
                  : "Save"}
            </button>
            <Link
              to="/cms/content-sections"
              className="btn btn-light ms-2 pt-1 pb-1 p-3"
            >
              Cancel
            </Link>
          </div>
        </div>
      </form>
    </>
  );
};
